import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, of, tap } from 'rxjs';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzRadioModule } from 'ng-zorro-antd/radio';

import {
  CampaignPastResult,
  ICampaignGlobalWinner,
  IPromotion,
  IPromotionsSummary,
  PromotionsService,
  PromotionStatus
} from 'core';
import { KorviuLoaderComponent, KorviuNoDataComponent, KorviuSelectComponent, TabsV2Component } from 'shared';
import { IFilter } from 'shared/types/filter.interface';
import { SortOrders } from 'shared/types/sort-order.enum';
import { ISorting } from 'shared/types/sorting.interface';
import { PromotionCardComponent } from '../promotion-card/promotion-card.component';
import { PromotionsSummaryComponent } from '../promotions-summary/promotions-summary.component';
import { RecentPromotionWinnersComponent } from '../recent-promotion-winners/recent-promotion-winners.component';

const PROMOTION_STATUS_TABS: { id: number; label: string; status: PromotionStatus }[] = [
  { id: 0, label: 'promotions.statusTabs.featured', status: 'featured' },
  { id: 1, label: 'promotions.statusTabs.participating', status: 'participating' },
  { id: 2, label: 'promotions.statusTabs.past', status: 'past' }
];

type PastResultFilter = CampaignPastResult | 'null';

const PAST_SORT_NEWEST_VALUE = `end_date|${SortOrders.DESCEND}`;
const PAST_SORT_OLDEST_VALUE = `end_date|${SortOrders.ASCEND}`;
const PROMOTIONS_PAGE_SIZE = 10;
const PAST_PROMOTIONS_PAGE_SIZE = 2;
const RECENT_WINNERS_PAGE_SIZE = 4;

interface IPromotionsPageMeta {
  count: number;
  totalPages: number;
  perPage: number;
}

@Component({
  selector: 'app-promotions-list',
  imports: [
    KorviuLoaderComponent,
    KorviuNoDataComponent,
    KorviuSelectComponent,
    NzCollapseModule,
    NzRadioModule,
    PromotionCardComponent,
    PromotionsSummaryComponent,
    RecentPromotionWinnersComponent,
    ReactiveFormsModule,
    TabsV2Component,
    TranslatePipe
  ],
  templateUrl: './promotions-list.component.html',
  styleUrl: './promotions-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromotionsListComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #promotionsService = inject(PromotionsService);
  #promotionsRequestId = 0;
  #featuredPageAfterLoad = 1;

  readonly pastSearchControl = new FormControl('', { nonNullable: true });
  readonly pastSortControl = new FormControl<string | null>(null);
  readonly pastResultControl = new FormControl<PastResultFilter | null>(null);
  readonly pastSortNewestValue = PAST_SORT_NEWEST_VALUE;
  readonly pastSortOldestValue = PAST_SORT_OLDEST_VALUE;
  readonly pastSelectOptions = [];
  readonly statusTabs = PROMOTION_STATUS_TABS;
  readonly selectedStatus = signal<PromotionStatus>('featured');
  readonly selectedStatusTabId = computed(() => {
    return this.statusTabs.find(tab => tab.status === this.selectedStatus())?.id ?? 0;
  });
  readonly isFeaturedTab = computed(() => this.selectedStatus() === 'featured');
  readonly isParticipatingTab = computed(() => this.selectedStatus() === 'participating');
  readonly isPastTab = computed(() => this.selectedStatus() === 'past');

  readonly promotions = signal<IPromotion[]>([]);
  readonly promotionsIsLoading = signal<boolean>(false);
  readonly promotionsLoadError = signal<boolean>(false);
  readonly isBannerVisible = signal<boolean>(true);
  readonly pageMeta = signal<IPromotionsPageMeta>({ count: 0, totalPages: 1, perPage: 10 });
  readonly currentPage = signal<number>(1);
  readonly featuredPage = signal<number>(1);
  readonly visiblePromotions = computed(() => {
    if (!this.isFeaturedTab()) {
      return this.promotions();
    }

    return this.promotions().slice(this.featuredPage() - 1, this.featuredPage());
  });
  readonly hasFeaturedCarousel = computed(() => this.isFeaturedTab() && this.pageMeta().count > 1);
  readonly featuredPosition = computed(() => (this.currentPage() - 1) * this.pageMeta().perPage + this.featuredPage());
  readonly canGoToPreviousFeatured = computed(() => this.featuredPage() > 1 || this.currentPage() > 1);
  readonly canGoToNextFeatured = computed(
    () => this.featuredPage() < this.promotions().length || this.currentPage() < this.pageMeta().totalPages
  );
  readonly pastVisibleRange = computed(() => {
    const count = this.pageMeta().count;

    if (!count) {
      return { start: 0, end: 0, count };
    }

    const start = (this.currentPage() - 1) * this.pageMeta().perPage + 1;
    const end = Math.min(start + this.promotions().length - 1, count);

    return { start, end, count };
  });
  readonly summary = signal<IPromotionsSummary | null>(null);
  readonly summaryIsLoading = signal<boolean>(false);
  readonly summaryLoadError = signal<boolean>(false);
  readonly recentWinners = signal<ICampaignGlobalWinner[]>([]);
  readonly recentWinnersIsLoading = signal<boolean>(false);

  public ngOnInit(): void {
    this.#initPastControls();
    this.loadPromotions();
    this.loadSummary();
    this.loadRecentWinners();
  }

  public onStatusTabChange(tabId: number): void {
    const status = this.statusTabs.find(tab => tab.id === tabId)?.status;

    if (!status || status === this.selectedStatus()) {
      return;
    }

    this.selectedStatus.set(status);
    this.currentPage.set(1);
    this.featuredPage.set(1);
    this.#featuredPageAfterLoad = 1;
    this.loadPromotions();
  }

  public onPastPageChange(page: number): void {
    const totalPages = this.pageMeta().totalPages;

    if (page < 1 || page > totalPages || page === this.currentPage()) {
      return;
    }

    this.currentPage.set(page);
    this.loadPromotions();
  }

  public onFeaturedPageChange(page: number): void {
    if (this.promotionsIsLoading() || !this.isFeaturedTab() || page === this.featuredPage()) {
      return;
    }

    if (page < 1 && this.canGoToPreviousFeatured()) {
      this.currentPage.update(current => current - 1);
      this.#featuredPageAfterLoad = -1;
      this.loadPromotions();
      return;
    }

    if (page > this.promotions().length && this.canGoToNextFeatured()) {
      this.currentPage.update(current => current + 1);
      this.#featuredPageAfterLoad = 1;
      this.loadPromotions();
      return;
    }

    if (page < 1 || page > this.promotions().length) {
      return;
    }

    this.featuredPage.set(page);
  }

  public closeBanner(): void {
    this.isBannerVisible.set(false);
  }

  public loadPromotions(): void {
    const requestId = ++this.#promotionsRequestId;
    const pageSize = this.isPastTab() ? PAST_PROMOTIONS_PAGE_SIZE : PROMOTIONS_PAGE_SIZE;

    this.promotionsIsLoading.set(true);
    this.promotionsLoadError.set(false);

    this.#promotionsService
      .getPromotions(
        this.selectedStatus(),
        { page: this.currentPage(), pageSize },
        this.#getPastFilters(),
        this.#getPastSorting()
      )
      .pipe(
        tap(response => {
          if (requestId !== this.#promotionsRequestId) {
            return;
          }

          this.promotions.set(response.results ?? []);
          this.featuredPage.set(this.#featuredPageAfterLoad === -1 ? Math.max(1, this.promotions().length) : 1);
          this.#featuredPageAfterLoad = 1;
          this.pageMeta.set({
            count: response.count ?? 0,
            totalPages: response.total_pages ?? 1,
            perPage: response.per_page ?? 10
          });
        }),
        catchError(() => {
          if (requestId !== this.#promotionsRequestId) {
            return EMPTY;
          }

          this.promotions.set([]);
          this.pageMeta.set({ count: 0, totalPages: 1, perPage: pageSize });
          this.promotionsLoadError.set(true);
          return EMPTY;
        }),
        finalize(() => {
          if (requestId !== this.#promotionsRequestId) {
            return;
          }

          this.promotionsIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public loadSummary(): void {
    if (this.summaryIsLoading()) {
      return;
    }

    this.summaryIsLoading.set(true);
    this.summaryLoadError.set(false);

    this.#promotionsService
      .getPromotionsSummary()
      .pipe(
        tap(summary => {
          this.summary.set(summary);
        }),
        catchError(() => {
          this.summary.set(null);
          this.summaryLoadError.set(true);
          return of(null);
        }),
        finalize(() => {
          this.summaryIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  private loadRecentWinners(): void {
    this.recentWinnersIsLoading.set(true);

    this.#promotionsService
      .getGlobalCampaignWinners({ page: 1, pageSize: RECENT_WINNERS_PAGE_SIZE })
      .pipe(
        tap(response => {
          this.recentWinners.set(response.results);
        }),
        catchError(() => {
          this.recentWinners.set([]);
          return EMPTY;
        }),
        finalize(() => {
          this.recentWinnersIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #getPastFilters(): IFilter[] | undefined {
    if (!this.isPastTab()) {
      return undefined;
    }

    const filters: IFilter[] = [];
    const search = this.pastSearchControl.value.trim();
    const result = this.pastResultControl.value;

    if (search) {
      filters.push({ name: 'search', value: search });
    }

    if (result) {
      filters.push({ name: 'result', value: result });
    }

    return filters;
  }

  #getPastSorting(): ISorting | undefined {
    const value = this.isPastTab() ? this.pastSortControl.value : null;

    if (!value) {
      return undefined;
    }

    const [sortBy, sortOrder] = value.split('|');

    return {
      sortBy,
      sortOrder: sortOrder as SortOrders
    };
  }

  #initPastControls(): void {
    this.pastSearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#reloadPastFromFirstPage();
      });

    this.pastSortControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#reloadPastFromFirstPage();
      });

    this.pastResultControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#reloadPastFromFirstPage();
      });
  }

  #reloadPastFromFirstPage(): void {
    if (!this.isPastTab()) {
      return;
    }

    this.currentPage.set(1);
    this.loadPromotions();
  }
}
